import React, { useRef, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Checkbox,
  Typography,
  Grid,
} from "@mui/material";
import { styled } from "@mui/system";
import { CardHeader } from "@mui/material";
import { getDataPlanCode } from "../Payment/PaymentService/paymentService";

const ScrollableCreditCard = ({
  listData,
  cardData,
  setListData,
  fullName,
  cardNumber,
  month,
  year,
  type,
  bank,
  relation,
  dataPolicy,
  fullNameRelate,
  dataPolicies
}) => {
  const cardRef = useRef(null);
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [allowedBaseCodes, setAllowedBaseCodes] = useState(null); // null = ยังโหลดไม่เสร็จ

  useEffect(() => {
    const selectedIndexList = listData.map((item) => item.index);
    setSelectedIndexes(selectedIndexList);
  }, [cardData, listData, dataPolicy]);

  useEffect(() => {
    if (dataPolicy?.length > 0) {
      getDataPlanCodeApi();
    }
  }, [dataPolicy]);

  const getDataPlanCodeApi = async () => {
    const results = await Promise.all(
      dataPolicy.map((p) => getDataPlanCode(p.baseCode))
    );
    const allowed = new Set(
      dataPolicy
        .filter((p, i) => (results[i]?.data?.length ?? 0) > 0)
        .map((p) => p.baseCode)
    );
    setAllowedBaseCodes(allowed);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    // ถ้า DB ส่งมาเป็น MM/YY หรือ MM/YYYY ตรงๆ ให้ใช้ค่านั้นเลย
    const slashMatch = String(dateString).match(/^(\d{2})\/(\d{2,4})$/);
    if (slashMatch) {
      const mm = slashMatch[1];
      const yy = slashMatch[2].slice(-2);
      return `${mm}/${yy}`;
    }
    // fallback: parse เป็น Date object (กรณีเป็น ISO string)
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "-";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}/${yy}`;
  };

  const partialHide = (num) => {
    if (!num || typeof num !== "string") return "-";
    const arr = num.split("");
    arr.splice(4, 8, "********");
    return arr.join("");
  };

  // เมื่อข้อมูลฟอร์มด้านซ้ายเปลี่ยน ให้ sync ค่าใน listData ที่เลือกไว้
  useEffect(() => {
    if (!listData?.length) return;
    const updated = listData.map((item) => ({
      ...item,
      policy_no: item.policy_no?.trim() ?? "",
      card_holder_name: fullName,
      card_number: cardNumber,
      card_expire_date: month && year ? `${month}/${year}` : "",
      card_type: type,
      credit_bank_name: bank,
      card_relation: relation,
      card_relation_name: fullNameRelate,
    }));
    setListData(updated);
  }, [fullName, cardNumber, month, year, type, bank, relation, fullNameRelate]); // eslint-disable-line

  const CustomCheckbox = styled(Checkbox)({
    color: "rgba(255, 94, 67, 0.5)",
    "&.Mui-checked": { color: "#FF5E43" },
    marginRight: "15px",
  });

  const getCardTypeNameByCode = (code) => {
    const cardTypes = [
      { value: "AM", label: "Amex" },
      { value: "C4", label: "JCB" },
      { value: "MC", label: "Mastercard" },
      { value: "VI", label: "Visa" },
    ];
    const f = cardTypes.find((i) => i.value === code);
    return f ? f.label : code || "-";
  };

  // ✅ toggle แบบหลายรายการ (ไม่ล้างตัวเดิม)
  const handleToggleSelect = (
    index,
    policy_no,
    old_card_holder_name,
    old_card_number,
    old_card_expire_date,
    old_card_type,
    old_credit_bank_name,
    premium_amount,
    next_premium_due,
    old_card_relation
  ) => {
    const policyTrim = (policy_no || "").trim();

    // 1) toggle visual index
    setSelectedIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );

    // 2) toggle listData ตาม policy_no (กันซ้ำด้วย policy_no)
    setListData((prevList) => {
      const exists = prevList.some(
        (it) => (it.policy_no || "").trim() === policyTrim
      );
      if (exists) {
        return prevList.filter(
          (it) => (it.policy_no || "").trim() !== policyTrim
        );
      }
      const newItem = {
        index,
        policy_no: policyTrim,
        card_holder_name: fullName,
        card_number: cardNumber,
        card_expire_date: month && year ? `${month}/${year}` : "",
        card_type: type,
        credit_bank_name: bank,
        card_relation: relation,
        premium_amount,
        next_premium_due,
        old_card_holder_name: old_card_holder_name || "",
        old_card_number: old_card_number || "",
        old_card_expire_date: old_card_expire_date || "",
        old_card_type: old_card_type || "",
        old_credit_bank_name: old_credit_bank_name || "",
        old_card_relation: old_card_relation || "",
        card_relation_name: fullNameRelate,
      };
      return [...prevList, newItem];
    });
  };

  if (!dataPolicy || dataPolicy.length === 0) {
    return (
      <Card sx={{ borderRadius: "12px", border: "2px solid rgba(255, 94, 67, 0.3)", boxShadow: "none" }}>
        <CardContent sx={{ textAlign: "center", py: 4 }}>
          <Typography sx={{ color: "red", fontFamily: "Prompt-Thin" }}>
            ไม่พบข้อมูลกรมธรรม์
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card ref={cardRef} sx={{ maxHeight: "490px", overflowY: "auto" }}>
      <CardContent
        sx={{
          maxHeight: "490px",
          overflowY: "auto",
          border: "1px solid rgba(255, 94, 67, 0.3)",
          borderRadius: "12px",
          borderWidth: "2px",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography variant="body2" sx={{ color: "red", mt: "5px", ml: "5px" }}>
          *กรุณาเลือกกรมธรรม์
        </Typography>

        {dataPolicy.map((policy, index) => {
          const card = cardData?.[index];
          const isDisabled = allowedBaseCodes !== null && !allowedBaseCodes.has(policy.baseCode);
          const isSelected = !isDisabled && selectedIndexes.includes(index);

          return (
            <Box
              key={index}
              onClick={() => {
                if (isDisabled) return;
                handleToggleSelect(
                  index,
                  policy?.policyNo,
                  card?.card_holder_name,
                  card?.card_number,
                  card?.card_expire_date,
                  card?.card_type,
                  card?.credit_bank_name,
                  card?.premium_amount,
                  card?.next_premium_due,
                  card?.card_relation
                );
              }}
              sx={{
                mb: "15px",
                p: "10px",
                transition: "background-color 0.3s ease, border 0.3s ease",
                cursor: isDisabled ? "not-allowed" : "pointer",
                alignItems: "center",
                justifyContent: "space-between",
                border: isSelected ? "2px solid #FF5E43" : "1px solid #e0e0e0",
                borderRadius: "8px",
                opacity: isDisabled ? 0.6 : 1,
              }}
            >
              <Card
                style={{ zIndex: 1000 }}
                sx={{
                  backgroundColor: isDisabled
                    ? "rgba(0, 0, 0, 0.08)"
                    : isSelected
                    ? "rgba(255, 94, 67, 0.1)"
                    : "transparent",
                  transition: "background-color 0.3s ease, border 0.3s ease",
                }}
              >
                <CardHeader
                  title={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CustomCheckbox
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (isDisabled) return;
                          handleToggleSelect(
                            index,
                            policy?.policyNo,
                            card?.card_holder_name,
                            card?.card_number,
                            card?.card_expire_date,
                            card?.card_type,
                            card?.credit_bank_name,
                            card?.premium_amount,
                            card?.next_premium_due,
                            card?.card_relation
                          );
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        sx={{ color: "rgba(255, 94, 67, 0.8)", mr: 1 }}
                      />

                      <Box>
                        <Box>
                          {"กรมธรรม์เลขที่ : " + (policy?.policyNo || "-")}
                        </Box>
                        <Box sx={{ mt: "4px" }}>
                          {"ชื่อแบบประกัน : " + (policy?.baseName || "-")}
                        </Box>
                      </Box>
                    </Box>
                  }
                  sx={{ backgroundColor: "rgba(255, 94, 67, 0.3)" }}
                  titleTypographyProps={{
                    sx: {
                      fontFamily: '"Prompt-Thin", sans-serif !important',
                      fontSize: "15px",
                      color: "black",
                      ml: "15px",
                    },
                  }}
                />

                <CardContent>
                  <Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4} md={3}>
                        <span className="font-size-label-pol">
                          ชื่อ นามสกุล เจ้าของบัตรเครดิต :
                        </span>
                      </Grid>
                      <Grid item xs={12} sm={8} md={9}>
                        <div
                          style={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          <span className="font-size-label-pol">
                            {card?.card_holder_name || "-"}
                          </span>
                        </div>
                      </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4} md={3}>
                        <span className="font-size-label-pol">
                          หมายเลขบัตร :
                        </span>
                      </Grid>
                      <Grid item xs={12} sm={8} md={9}>
                        <span className="font-size-label-pol">
                          {card?.card_number ? partialHide(card.card_number) : "-"}
                        </span>
                      </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4} md={3}>
                        <span className="font-size-label-pol">
                          เดือน/ปี หมดอายุของบัตร :
                        </span>
                      </Grid>
                      <Grid item xs={12} sm={8} md={9}>
                        <span className="font-size-label-pol">
                          {card?.card_expire_date ? formatDate(card.card_expire_date) : "-"}
                        </span>
                      </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4} md={3}>
                        <span className="font-size-label-pol">
                          ประเภทของบัตร :
                        </span>
                      </Grid>
                      <Grid item xs={12} sm={8} md={9}>
                        <span className="font-size-label-pol">
                          {card?.card_type ? getCardTypeNameByCode(card.card_type) : "-"}
                        </span>
                      </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4} md={3}>
                        <span className="font-size-label-pol">
                          ธนาคารเจ้าของบัตรเครดิต :
                        </span>
                      </Grid>
                      <Grid item xs={12} sm={8} md={9}>
                        <span className="font-size-label-pol">
                          {card?.credit_bank_name || "-"}
                        </span>
                      </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={5} md={3}>
                        <span className="font-size-label-pol">
                          ความสัมพันธ์กับผู้เอาประกัน :
                        </span>
                      </Grid>
                      <Grid item xs={12} sm={7} md={9}>
                        <span className="font-size-label-pol">
                          {card?.card_relation || "-"}
                        </span>
                      </Grid>
                    </Grid>
                  </Typography>
                  {isDisabled && (
                    <Typography sx={{ color: "red", fontSize: "13px", mt: "8px", fontFamily: "Prompt-Thin" }}>
                      *แบบประกันของท่าน ไม่รองรับการชำระเบี้ยผ่านบัตรเครดิตอัตโนมัติ กรุณา ติดต่อฝ่ายลูกค้าสัมพันธ์
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default ScrollableCreditCard;
